import { Organization, UpdateOrganizationPayload } from "@cohesive/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { orgAPI } from "./api";

const QUERY_KEYS = {
  organization: ["organization"] as const,
};

/**
 * Get organization details
 */
export function useOrganization() {
  return useQuery({
    queryKey: QUERY_KEYS.organization,
    queryFn: async () => {
      return await orgAPI.organization.get();
    },
  });
}

/**
 * Update organization
 */
export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateOrganizationPayload) => {
      return await orgAPI.organization.update(payload);
    },
    onSuccess: (updatedOrganization: Organization) => {
      // Update the cached organization data
      queryClient.setQueryData(QUERY_KEYS.organization, updatedOrganization);

      // Invalidate related queries that might depend on organization data
      queryClient.invalidateQueries({
        queryKey: ["user-info"],
      });
    },
    onError: (error) => {
      console.error("Failed to update organization:", error);
      toast.error("Failed to update organization");
    },
  });
}

/**
 * Delete organization
 */
export function useDeleteOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return await orgAPI.organization.delete();
    },
    onSuccess: () => {
      // Clear organization cache
      queryClient.removeQueries({
        queryKey: QUERY_KEYS.organization,
      });

      // Invalidate user info as well
      queryClient.invalidateQueries({
        queryKey: ["user-info"],
      });
    },
    onError: (error) => {
      console.error("Failed to delete organization:", error);
      toast.error("Failed to delete organization");
    },
  });
}
